import { useState } from 'react';
import {
  Panel,
  PanelHeader,
  Group,
  FormItem,
  Input,
  Button,
  Snackbar,
} from '@vkontakte/vkui';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVerify } from '../api/hooks';
import { allowNotifications, openUrl } from '../utils/vkBridge';
import { api } from '../api/client';
import { REGISTER_URL } from '../constants/registerUrl';
import { FlowPreviewBanner } from '../components/FlowPreviewBanner';

export function LoginPage({
  previewMode,
  onExitPreview,
  onBackClick,
  onLoginSuccess,
}: {
  previewMode?: boolean;
  onExitPreview?: () => void;
  onBackClick?: () => void;
  onLoginSuccess?: () => void;
} = {}) {
  const [idPervye, setIdPervye] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const verify = useVerify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const submit = async () => {
    setError(null);
    try {
      const res = await verify.mutateAsync({
        idPervye: parseInt(idPervye, 10),
        email: email.trim(),
      });
      await queryClient.refetchQueries({ queryKey: ['me'] });
      setSnack(
        res.licenseLogin
          ? 'Лицензия выдана! Добро пожаловать.'
          : 'Вход выполнен. Добро пожаловать.',
      );
      void (async () => {
        if (await allowNotifications()) {
          await api('/api/notifications/allow', { method: 'POST' }).catch(() => {});
        }
      })();
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        navigate('/profile', { replace: true });
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const openRegister = async () => {
    const url = REGISTER_URL;
    const ok = await openUrl(url);
    if (!ok) {
      setSnack(
        'Не удалось открыть сайт внутри VK. Скопируйте ссылку и откройте в браузере.',
      );
      await navigator.clipboard.writeText(url).catch(() => {});
    }
  };

  const goBack = () => {
    if (onBackClick) onBackClick();
    else navigate('/');
  };

  return (
    <Panel id="login">
      <PanelHeader
        before={
          <Button mode="tertiary" onClick={onExitPreview ?? goBack}>
            {onExitPreview ? 'Выйти' : 'Назад'}
          </Button>
        }
      >
        Вход
      </PanelHeader>
      {previewMode && (
        <Group style={{ margin: '0 16px' }}>
          <FlowPreviewBanner />
        </Group>
      )}
      <Group className="pb-glow-card" style={{ margin: 16 }}>
        <FormItem top="ID Первых" status={error ? 'error' : 'default'}>
          <Input
            type="number"
            inputMode="numeric"
            value={idPervye}
            onChange={(e) => setIdPervye(e.target.value)}
            placeholder="Например, 9000001"
          />
        </FormItem>
        <FormItem top="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
          />
        </FormItem>
        {error && (
          <FormItem>
            <span style={{ color: 'var(--vkui--color_text_negative)' }}>{error}</span>
          </FormItem>
        )}
        <FormItem>
          <Button
            size="l"
            stretched
            className="pb-gradient-btn"
            loading={verify.isPending}
            onClick={submit}
          >
            Войти
          </Button>
        </FormItem>
        <FormItem>
          <Button size="l" stretched mode="secondary" onClick={openRegister}>
            Зарегистрироваться
          </Button>
        </FormItem>
      </Group>
      {snack && <Snackbar onClose={() => setSnack(null)}>{snack}</Snackbar>}
    </Panel>
  );
}
